import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FiCheckCircle } from "react-icons/fi";
import { FaInstagram, FaFacebookF, FaLinkedinIn, FaYoutube, FaTwitter, FaGoogle } from "react-icons/fa";

import TextileBackground from "../../components/TextileBackground";
import CustomNavbar from "../../components/Navbar";
import FooterLinks from "../../components/FooterLinks";
import Footer from "../../components/Footer";
import SectionDivider from "../../components/SectionDivider";
import Reveal from "../../components/Reveal";
import { EASE, DURATION } from "../../lib/motion";
import brands from "../../data/brands";
import catalogue from "../../data/catalogue";
import company from "../../data/company";

const PRODUCTS = [
  ...catalogue.women.map((item) => ({ ...item, href: "/catalogue/women" })),
  ...catalogue.men.map((item) => ({ ...item, href: "/catalogue/men" })),
];

const SOCIAL_ICONS = [
  { Icon: FaInstagram, href: company.social.instagram, label: "Instagram" },
  { Icon: FaFacebookF, href: company.social.facebook, label: "Facebook" },
  { Icon: FaLinkedinIn, href: company.social.linkedin, label: "LinkedIn" },
  { Icon: FaYoutube, href: company.social.youtube, label: "YouTube" },
  { Icon: FaTwitter, href: company.social.twitter, label: "Twitter" },
  { Icon: FaGoogle, href: company.social.google, label: "Google" },
];

function BrandPanel({ brand, index }) {
  const isActive = brand.id === "sutramay";
  const isAvailable = !!brand.href;

  const panel = (
    <div
      className={`group relative aspect-[16/10] sm:aspect-[4/3] rounded-[2rem] overflow-hidden border transition-all duration-500 ${
        isActive
          ? "bg-gradient-to-br from-maroon-50 to-maroon-100 border-gold-500 ring-2 ring-gold-500/70 shadow-lg"
          : "bg-gradient-to-br from-maroon-50 to-beige-100 border-maroon-200/60"
      } ${isAvailable ? "hover:shadow-xl hover:-translate-y-1 hover:border-gold-500" : "opacity-80"}`}
    >
      {/* Watermark monogram */}
      <span
        aria-hidden="true"
        className="absolute -right-4 -bottom-6 font-heading text-[9rem] sm:text-[11rem] leading-none text-maroon-800/[0.06] select-none"
      >
        {brand.name.charAt(0)}
      </span>

      <div className="relative h-full flex flex-col items-center justify-center text-center px-6">
        {isActive && (
          <span className="absolute top-4 left-1/2 -translate-x-1/2 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-cream-50/90 border border-gold-500/60 text-gold-700 font-accent text-[10px] tracking-[0.15em] uppercase">
            <FiCheckCircle className="text-xs" /> Currently Viewing
          </span>
        )}
        {!isAvailable && (
          <span className="absolute top-4 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-cream-50/80 border border-maroon-300/50 text-maroon-700/85 font-accent text-[10px] tracking-[0.15em] uppercase">
            Coming Soon
          </span>
        )}

        <h3 className="font-heading uppercase text-maroon-800 text-2xl sm:text-3xl tracking-[0.1em]">
          {brand.name}
        </h3>
        <span
          className={`mt-3 h-px w-16 bg-gold-500/60 transition-all duration-500 ${
            isAvailable ? "group-hover:w-24" : ""
          }`}
        />
      </div>
    </div>
  );

  return (
    <Reveal delay={index * 150}>
      {isAvailable ? (
        <Link to={brand.href} className="block" aria-current={isActive ? "page" : undefined}>
          {panel}
        </Link>
      ) : (
        <div aria-disabled="true" title={`${brand.name} — coming soon`}>
          {panel}
        </div>
      )}
    </Reveal>
  );
}

function ProductPanel({ item, index }) {
  return (
    <Reveal delay={index * 90}>
      <Link
        to={item.href}
        className="group relative flex flex-col items-center justify-center text-center aspect-square rounded-3xl overflow-hidden border border-maroon-200/60 bg-gradient-to-br from-maroon-50 to-maroon-100/70 px-4 transition-all duration-500 hover:shadow-lg hover:-translate-y-1 hover:border-gold-500"
      >
        <item.Icon className="text-maroon-700/85 text-2xl sm:text-3xl mb-3 transition-transform duration-500 group-hover:scale-110" />
        <p className="font-heading font-semibold text-maroon-800 text-xs sm:text-sm uppercase tracking-wide leading-snug">
          {item.name}
        </p>
      </Link>
    </Reveal>
  );
}

export default function Catalogue() {
  return (
    <TextileBackground>
      <CustomNavbar />

      <div className="px-6 pt-16 pb-10 text-center">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: DURATION, ease: EASE }}
          className="font-accent text-maroon-700 text-xs tracking-[0.35em] uppercase"
        >
          Sutramay Industries
        </motion.p>
        <motion.h1
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: DURATION, ease: EASE, delay: 0.15 }}
          className="font-heading uppercase text-maroon-800 text-4xl sm:text-5xl mt-2"
        >
          Shopping Catalogue
        </motion.h1>
      </div>

      <section className="max-w-5xl mx-auto px-6 pb-16">
        <Reveal>
          <p className="font-accent text-gold-700 text-xs tracking-[0.3em] uppercase text-center mb-6">
            Choose Brand
          </p>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
          {brands.map((brand, i) => (
            <BrandPanel key={brand.id} brand={brand} index={i} />
          ))}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 pb-16">
        <Reveal>
          <p className="font-accent text-gold-700 text-xs tracking-[0.3em] uppercase text-center mb-2">
            What We Manufacture
          </p>
        </Reveal>
        <Reveal delay={80}>
          <h2 className="font-heading text-maroon-800 text-2xl sm:text-3xl text-center mb-10">Our Products</h2>
        </Reveal>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 sm:gap-6">
          {PRODUCTS.map((item, i) => (
            <ProductPanel key={item.name} item={item} index={i} />
          ))}
        </div>
        <Reveal delay={200}>
          <p className="font-body text-maroon-900/85 text-xs text-center mt-8 max-w-xl mx-auto leading-relaxed">
            These are manufacturing categories, not products available for direct online purchase.
          </p>
        </Reveal>
      </section>

      <div className="px-6 py-16 text-center min-h-[40vh] flex flex-col items-center justify-center">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: DURATION, ease: EASE }}
          className="font-accent text-maroon-700 text-xs tracking-[0.35em] uppercase"
        >
          The Catalogue
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: DURATION, ease: EASE, delay: 0.15 }}
          className="font-heading uppercase text-maroon-800 text-4xl sm:text-5xl mt-2"
        >
          Coming Soon
        </motion.h2>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: DURATION, ease: EASE, delay: 0.3 }}
          className="mt-6"
        >
          <SectionDivider />
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: DURATION, ease: EASE, delay: 0.45 }}
          className="font-body text-maroon-900/90 text-sm sm:text-base leading-relaxed mt-6 max-w-xl"
        >
          Our full catalogue is on its way. Please check back soon.
        </motion.p>
      </div>

      <section className="px-6 pb-16 flex flex-col items-center">
        <Reveal>
          <p className="text-center font-accent text-maroon-500 text-xs tracking-[0.35em] uppercase mb-6">
            Follow Us
          </p>
        </Reveal>
        <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6">
          {SOCIAL_ICONS.map(({ Icon, href, label }, i) => (
            <Reveal key={label} delay={i * 100}>
              <a
                href={href}
                aria-label={label}
                className="flex items-center justify-center h-11 w-11 sm:h-12 sm:w-12 rounded-full border border-gold-500 text-gold-500 bg-maroon-800 hover:bg-gold-500 hover:text-maroon-900 hover:scale-110 transition-all duration-500 shadow-md"
              >
                <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
              </a>
            </Reveal>
          ))}
        </div>
      </section>

      <FooterLinks />
      <Footer />
    </TextileBackground>
  );
}
